/** Czech public holidays (státní svátky) including movable Easter days. */

export interface CzechHoliday {
  month: number; // 1–12
  day: number;
  name: string;
}

/** Anonymous Gregorian algorithm → Easter Sunday (month 1–12). */
function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

function addDays(year: number, month: number, day: number, delta: number): {
  year: number;
  month: number;
  day: number;
} {
  const d = new Date(year, month - 1, day + delta);
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

const FIXED_HOLIDAYS: CzechHoliday[] = [
  { month: 1, day: 1, name: 'Nový rok' },
  { month: 5, day: 1, name: 'Svátek práce' },
  { month: 5, day: 8, name: 'Den vítězství' },
  { month: 7, day: 5, name: 'Cyril a Metoděj' },
  { month: 7, day: 6, name: 'Jan Hus' },
  { month: 9, day: 28, name: 'Den české státnosti' },
  { month: 10, day: 28, name: 'Vznik Československa' },
  { month: 11, day: 17, name: 'Den boje za svobodu a demokracii' },
  { month: 12, day: 24, name: 'Štědrý den' },
  { month: 12, day: 25, name: '1. svátek vánoční' },
  { month: 12, day: 26, name: '2. svátek vánoční' },
];

/** All Czech public holidays for a given year (fixed + Easter). */
export function getCzechHolidays(year: number): CzechHoliday[] {
  const easter = easterSunday(year);
  const goodFriday = addDays(year, easter.month, easter.day, -2);
  const easterMonday = addDays(year, easter.month, easter.day, 1);

  const movable: CzechHoliday[] = [];
  if (goodFriday.year === year) {
    movable.push({
      month: goodFriday.month,
      day: goodFriday.day,
      name: 'Velký pátek',
    });
  }
  if (easterMonday.year === year) {
    movable.push({
      month: easterMonday.month,
      day: easterMonday.day,
      name: 'Velikonoční pondělí',
    });
  }

  return [...FIXED_HOLIDAYS, ...movable];
}

/** Map day-of-month → holiday name for a given year+month. */
export function getHolidaysInMonth(
  year: number,
  month: number
): Map<number, string> {
  const map = new Map<number, string>();
  for (const h of getCzechHolidays(year)) {
    if (h.month === month) {
      map.set(h.day, h.name);
    }
  }
  return map;
}

export function isCzechHoliday(
  year: number,
  month: number,
  day: number
): string | null {
  return getHolidaysInMonth(year, month).get(day) ?? null;
}
